





















































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { validate } from "vee-validate";
import { ValidationObserver } from "vee-validate";
import HasValidation from "@/mixins/HasValidation";
import { ICurrency } from "@/models/constants";

@Component
export default class CurrencyInput extends Mixins(HasValidation) {
  public declare $refs: {
    currencyInput: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };
  // Props
  @Prop({ required: true })
  public value!: number | null;

  @Prop({ required: true })
  public currency!: ICurrency;

  @Prop({ default: false })
  public disabled!: boolean;

  @Prop({ default: false })
  public autofocus!: boolean;

  @Prop({ default: false })
  public required!: boolean;

  @Prop({ default: 0.01 })
  public step!: number;

  @Prop({ default: null })
  public min!: number;

  @Prop({ default: null })
  public max!: number;

  @Prop({ default: true })
  public showCode!: boolean;

  @Prop({ default: false })
  public showFlag!: boolean;

  @Prop({ default: "price" })
  public inputId!: string;

  @Prop({ default: "_.price" })
  public inputName!: string;

  @Prop({ default: true })
  public showErrorMessage!: boolean;

  @Prop({ default: false })
  public isFocused!: boolean;

  // Data
  public inputVal = "";
  public errors: string[] = [];
  // Computed
  public get formattedValue() {
    return this.formatValue(this.value as number);
  }
  public get formattedMin() {
    return this.formatValue(this.min);
  }
  public get formattedMax() {
    return this.formatValue(this.max);
  }
  public get validationRules() {
    const rules: any = {};
    rules.required = this.required;
    if (this.min !== null) {
      rules.min_value = this.min;
    }
    if (this.max !== null) {
      rules.max_value = this.max;
    }
    return rules;
  }
  public get hasValidationErrors() {
    return this.errors[0] || this.hasError(this.inputId);
  }
  public get firstValidationError() {
    return this.errors[0] || this.getError(this.inputId)[0];
  }

  // Watchers
  @Watch("value", { immediate: true })
  public onValueChange(val: number | null, oldVal) {
    if (oldVal !== undefined) this.validateValue(val);
    const inputVal = this.$_.isNil(val) ? `` : `${val}`;
    if (inputVal === this.inputVal) return;
    this.onInput(inputVal);
  }

  @Watch("validationRules")
  public onRulesChange() {
    this.validateValue(this.value);
  }

  @Watch("observer.errors")
  public setErrors() {
    if (this.$_.isEmpty(this.observer)) return;
    this.$set(this.observer.errors, this.inputId, [
      ...this.errors,
      ...this.getError(this.inputId)
    ]);
  }
  // Methods

  public created() {
    this.$emit("created");
    if (!this.isFocused) return;

    this.$nextTick(() => {
      this.$refs.currencyInput.focus();
    });
  }
  public onFocus($event) {
    $event.target.select();
  }
  public onBlur() {
    this.$emit("blur", this.inputVal);
  }

  public onKeyPress($e) {
    // Abort if Ctrl+A | Ctrl+C | Ctrl+X | Ctrl+V
    const isCtrlKey = $e.ctrlKey || $e.metaKey;
    const isACVX = ["KeyA", "KeyC", "KeyV", "KeyX"].includes($e.code);
    if (isCtrlKey && isACVX) return true;
    const key = $e.key;
    const val = $e.target.value;
    const sS = $e.target.selectionStart;
    const sE = $e.target.selectionEnd;
    // Compute would-be future value
    let futureVal = val.split("");
    futureVal.splice(sS, sE - sS);
    futureVal = futureVal.join("");
    futureVal = [futureVal.slice(0, sS), key, futureVal.slice(sS)].join("");
    // Enforce valid future value (999,999,999,999.99 max)
    if (!futureVal.match(/^(-?)(\d{0,12})(\.\d{0,4})?$/))
      return $e.preventDefault();
    // Prevent invalid leading zeros pattern (eg. 010.45)
    if (futureVal.match(/^(?:-?)(0\d+)(?:\.\d{0,2})?$/))
      return $e.preventDefault();
  }

  public onInput(val: string) {
    if (val.length) {
      // Remove invalid chars
      val = val.replace(/[^\d\\.-]/g, "");
      // Remove instances of `-` not at the start
      val = val.replace(/(?!^-)-/g, "");
      // Match ##(N).## pattern
      const matches = val.match(/^(-?\d{1,12})(\d*)(\.\d{2})(\d*)$/);
      // Enforce pattern
      val = matches && matches.length ? matches[1] + matches[3] : val;
      // Update inputVal
      this.$set(this, "inputVal", val);
      // Parse value as number
      const floatVal = parseFloat(val);
      // Check valid number
      if (isNaN(floatVal)) return;
      // Abort if value unchanged
      if (floatVal === this.value) return;
      // Emit new value
      this.$emit("input", floatVal);
    } else {
      this.$emit("input", null);
    }
  }

  public stepValue(amount = this.step) {
    let val = (this.value || 0) + amount;
    if (Math.abs(val) >= Number.MAX_SAFE_INTEGER) return;
    if (
      typeof this.min === "number" &&
      val < (this.value || 0) &&
      val < this.min
    )
      return;
    if (
      typeof this.max === "number" &&
      val > (this.value || 0) &&
      val > this.max
    )
      return;
    val = Math.round((val + Number.EPSILON) * 100) / 100;
    this.$emit("input", val);
  }

  public formatValue(val: number) {
    const floatVal = parseFloat(`${val}`);
    if (isNaN(floatVal)) return "–";
    return this.$store.getters["utils/money/formatValue"](
      floatVal,
      this.currency.code
    );
  }

  public validateValue(value: number | null) {
    this.clearErrors();
    validate(value, this.validationRules, {
      customMessages: {
        required: this.$t("_validation.required", {
          _field_: this.$t(this.inputName)
        }) as string,
        min_value: this.$t("_validation.min_value_currency", {
          _field_: this.$t(this.inputName),
          _currency_: this.formattedMin
        }) as string,
        max_value: this.$t("_validation.max_value_currency", {
          _field_: this.$t(this.inputName),
          _currency_: this.formattedMax
        }) as string
      }
    }).then(result => {
      this.$set(this, "errors", result.errors);
      this.setErrors();
    });
  }
}
